/**
 * GearLearner — your own gearbox, measured while you drive.
 *
 * The catalogue of ICE cars in the pit box is a guess: manufacturers do not publish gear ratios in any
 * spec sheet you can find (checked: automobile-catalog is paywalled, carfolio omits them, SEAT's own
 * technical data sheet says "Manual 6v" and nothing else). But a car that answers OBD-II tells us the
 * two numbers that matter, every second: road speed (PID 010D) and engine rpm (PID 010C).
 *
 * Their quotient IS the whole driveline for the gear you are in:
 *
 *     ratio = km/h per rpm = (wheel circumference) / (gear ratio × final drive)
 *
 * so it is a constant while a gear is engaged, and it steps to a new constant every time you shift.
 * Gears are therefore clusters in one dimension, and the speed at which one cluster hands over to the
 * next is, literally, where YOUR car changes gear. No tyre size, no final drive, no rev limiter needed:
 * they are all already inside the number.
 *
 * What it refuses to do: guess. A clutch on its way in, a shift in progress, wheelspin and a torque
 * converter all break the constant, so a sample only counts when the ratio has held still across
 * three readings. An EV (rpm always 0), a CVT (a continuum, not steps) and a car that never answers
 * simply never produce gears, and the UI keeps quiet instead of inventing a gearbox.
 */

/** One gear as measured. `topKmh` is where the driver actually leaves it. */
export interface LearnedGear {
  /** km/h per rpm — the constant that identifies this gear */
  ratio: number;
  /** where this gear hands over: the median upshift speed, or the fastest it was actually revved */
  topKmh: number;
  /** how the top speed was arrived at (shown to the driver, never hidden) */
  from: 'shifts' | 'habit' | 'revs';
  /** upshifts observed out of this gear */
  shifts: number;
  samples: number;
  /** highest rpm ever seen in this gear */
  maxRpm: number;
}

export interface LearnState {
  gears: LearnedGear[];
  /** samples that passed every gate */
  samples: number;
  /** highest rpm seen in any gear — the closest thing to the car's own limiter */
  maxRpm: number;
  /** enough to offer it to the driver */
  ready: boolean;
  /** seconds of qualifying driving so far */
  seconds: number;
}

interface Cluster {
  sum: number;      // Σ ratio, for the running mean
  n: number;
  maxRpm: number;
  /** rpm the driver was pulling when they left this gear, one per upshift */
  shiftRpms: number[];
  runMaxRpm: number;   // highest rpm during the current stay in this gear
}

const MIN_SPEED_KMH = 12;     // OBD speed arrives in whole km/h: below this, ±0.5 km/h IS the signal
const MIN_RPM = 900;          // idle and below: nothing to learn
const MAX_RPM = 9500;         // above any road car: a dongle glitch, not an engine
const RATIO_MIN = 0.002;      // 20 km/h at 8000 rpm — shorter than any road car's first
const RATIO_MAX = 0.09;       // 270 km/h at 3000 rpm — longer than any road car's top
// TWO readings, not three: at 2 Hz a third reading means a gear has to be held a second and a half to
// count, which quietly threw away every short shift and left the ladder biased towards the one spirited
// pull of the drive (measured: tops came out 25 % high). A shift moves the ratio by tens of percent
// between consecutive readings, so two is already enough to refuse one.
const STABLE_N = 2;           // readings the ratio must hold still across
const STABLE_TOL = 0.025;     // ±2.5 % counts as "held still"
const CLUSTER_TOL = 0.04;     // within ±4 % of a cluster's centre is the same gear
const MERGE_TOL = 0.03;
const MIN_SAMPLES = 12;       // a cluster below this is noise, not a gear
const MAX_PAIR_AGE_MS = 300;  // speed and rpm must describe the same moment
const MAX_AGE_GAP_MS = 200;
const MAX_GEARS = 9;          // more than this and it is not a stepped gearbox (CVT, or garbage)

export class GearLearner {
  private clusters: Cluster[] = [];
  private recent: { ratio: number; speed: number; t: number }[] = [];
  private engaged = -1;
  private engagedAt = 0;
  private lastT = 0;
  private lastAcceptedT = 0;
  private msDriven = 0;
  private accepted = 0;
  private topRpm = 0;

  /**
   * One reading pair. `speedAgeMs` / `rpmAgeMs` are how old each number was when the bridge sent it:
   * an OBD dongle answers one PID at a time, so the two are never read at the same instant and a pair
   * measured 400 ms apart during a hard pull is not a ratio, it is a smear.
   */
  feed(speedKmh: number, rpm: number, tMs: number, speedAgeMs = 0, rpmAgeMs = 0): void {
    if (!Number.isFinite(speedKmh) || !Number.isFinite(rpm) || !Number.isFinite(tMs)) return;
    if (speedAgeMs > MAX_PAIR_AGE_MS || rpmAgeMs > MAX_PAIR_AGE_MS) return;
    if (Math.abs(speedAgeMs - rpmAgeMs) > MAX_AGE_GAP_MS) return;
    if (speedKmh < MIN_SPEED_KMH || rpm < MIN_RPM || rpm > MAX_RPM) { this.recent.length = 0; return; }
    const ratio = speedKmh / rpm;
    if (ratio < RATIO_MIN || ratio > RATIO_MAX) { this.recent.length = 0; return; }

    this.recent.push({ ratio, speed: speedKmh, t: tMs });
    if (this.recent.length > STABLE_N) this.recent.shift();
    if (this.recent.length < STABLE_N) return;

    // held still across the window? (a shift, a slipping clutch or wheelspin fails here). The tolerance
    // has to breathe with speed: PID 010D answers in WHOLE km/h, so at 15 km/h a single count of
    // rounding is already 3 % of the reading, while a real shift moves the ratio by 20 % at the very
    // least. A fixed 2.5 % would have thrown first gear away on every car.
    const rs = this.recent.map((r) => r.ratio);
    const mid = [...rs].sort((a, b) => a - b)[Math.floor(STABLE_N / 2)];
    const tol = STABLE_TOL + 1 / speedKmh;
    for (const r of rs) if (Math.abs(r - mid) / mid > tol) return;

    // the bridge republishes its snapshot at 25 Hz while the dongle answers 1–3 times a second: without
    // this the same two readings would be counted twenty times and 'samples' would be a lie
    if (this.lastAcceptedT && tMs - this.lastAcceptedT < 200) return;
    if (this.lastT && tMs > this.lastT && tMs - this.lastT < 3000) this.msDriven += tMs - this.lastT;
    this.lastT = tMs;
    this.lastAcceptedT = tMs;
    this.accepted++;
    this.topRpm = Math.max(this.topRpm, rpm);

    const i = this.assign(mid);
    const c = this.clusters[i];
    c.sum += mid;
    c.n++;
    c.maxRpm = Math.max(c.maxRpm, rpm);
    c.runMaxRpm = Math.max(c.runMaxRpm, rpm);

    if (this.engaged !== i) {
      const prev = this.clusters[this.engaged];
      // an UPSHIFT out of the gear we were in (a longer gear = more km/h per rpm), close enough in time
      // to be the same manoeuvre: the speed it reached before handing over is the answer the pit box is
      // asking for. What is recorded is the RPM, not the speed: a driver shifts at roughly the same revs
      // in every gear, so the rpm generalises to the gears they never wrung out (nobody upshifts out of
      // sixth). Downshifts say nothing about where a gear ends, so they are ignored.
      const isUpshift = prev && this.center(c) > this.center(prev) * 1.05;
      if (isUpshift && prev.runMaxRpm > 0 && tMs - this.engagedAt < 4000) {
        prev.shiftRpms.push(prev.runMaxRpm);
      }
      if (prev) prev.runMaxRpm = 0;
      this.engaged = i;
    }
    this.engagedAt = tMs;
  }

  private center(c: Cluster): number { return c.sum / c.n; }

  private assign(ratio: number): number {
    let best = -1;
    let bestErr = CLUSTER_TOL;
    for (let i = 0; i < this.clusters.length; i++) {
      const err = Math.abs(this.center(this.clusters[i]) - ratio) / ratio;
      if (err < bestErr) { bestErr = err; best = i; }
    }
    if (best >= 0) return best;
    this.clusters.push({ sum: ratio, n: 1, maxRpm: 0, shiftRpms: [], runMaxRpm: 0 });
    return this.clusters.length - 1;
  }

  /** Clusters that drifted into each other are one gear seen twice. */
  private merged(): Cluster[] {
    const sorted = [...this.clusters].sort((a, b) => this.center(a) - this.center(b));
    const out: Cluster[] = [];
    for (const c of sorted) {
      const last = out[out.length - 1];
      if (last && Math.abs(this.center(last) - this.center(c)) / this.center(c) < MERGE_TOL) {
        last.sum += c.sum;
        last.n += c.n;
        last.maxRpm = Math.max(last.maxRpm, c.maxRpm);
        last.shiftRpms.push(...c.shiftRpms);
      } else {
        out.push({ ...c, shiftRpms: [...c.shiftRpms] });
      }
    }
    return out;
  }

  get state(): LearnState {
    const kept = this.merged().filter((c) => c.n >= MIN_SAMPLES);
    const median = (xs: number[]) => [...xs].sort((a, b) => a - b)[Math.floor(xs.length / 2)];
    // the driver's own habit: the revs they change gear at, over the whole box. It is what fills in the
    // gears they never wrung out, and the top gear, which by definition is never upshifted out of.
    const allShifts = kept.flatMap((c) => c.shiftRpms);
    const habitRpm = allShifts.length >= 2 ? median(allShifts) : 0;

    const build = (perGear: boolean): LearnedGear[] => kept.map((c) => {
      const ratio = this.center(c);
      const own = c.shiftRpms;
      const rpm = perGear && own.length >= 2 ? median(own) : (habitRpm || c.maxRpm);
      return {
        ratio,
        topKmh: Math.round(ratio * rpm),
        from: perGear && own.length >= 2 ? 'shifts' : habitRpm ? 'habit' : 'revs',
        shifts: own.length,
        samples: c.n,
        maxRpm: Math.round(c.maxRpm),
      };
    });

    const climbs = (gs: LearnedGear[]) => gs.every((g, i) => i === 0 || g.topKmh > gs[i - 1].topKmh);
    // measured gear by gear first; if that ladder does not climb (a driver who short-shifts second and
    // wrings out third), fall back to ONE rpm for the whole box, which always does
    let gears = build(true);
    if (!climbs(gears)) gears = build(false);

    return {
      gears,
      samples: this.accepted,
      maxRpm: Math.round(this.topRpm),
      seconds: Math.round(this.msDriven / 1000),
      ready: gears.length >= 3 && gears.length <= MAX_GEARS && climbs(gears),
    };
  }

  /**
   * WHICH GEAR THE CAR IS IN, right now — the whole point of measuring the ratios, and the thing no
   * OBD-II car will tell you (PID A4 exists since 2020 and almost nobody answers it). The engaged
   * cluster IS the engaged gear, so the sound can change gear at the same instant the driver does
   * instead of waiting for a speed to be crossed. 0 = not sure, and not sure means do not interfere.
   */
  engagedGear(nowMs: number, maxAgeMs = 2500): number {
    if (this.engaged < 0 || !this.lastAcceptedT) return 0;
    if (nowMs - this.lastAcceptedT > maxAgeMs) return 0;
    const here = this.center(this.clusters[this.engaged]);
    const kept = this.merged().filter((c) => c.n >= MIN_SAMPLES);
    if (kept.length < 3 || kept.length > MAX_GEARS) return 0;   // not a gearbox we understand yet
    for (let i = 0; i < kept.length; i++) {
      if (Math.abs(this.center(kept[i]) - here) / here < CLUSTER_TOL) return i + 1;
    }
    return 0;   // in a gear we have not learned yet
  }

  /** The ladder the pit box wants: one top speed per gear, km/h, rounded. */
  ladder(): number[] {
    return this.state.gears.map((g) => g.topKmh);
  }

  toJSON(): unknown {
    return {
      v: 1,
      clusters: this.clusters.map((c) => [c.sum, c.n, c.maxRpm, c.shiftRpms] as const),
      accepted: this.accepted,
      topRpm: this.topRpm,
      msDriven: this.msDriven,
    };
  }

  static fromJSON(raw: unknown): GearLearner {
    const l = new GearLearner();
    const o = raw as { v?: number; clusters?: [number, number, number, number[]][]; accepted?: number; topRpm?: number; msDriven?: number };
    if (!o || o.v !== 1 || !Array.isArray(o.clusters)) return l;
    l.clusters = o.clusters
      .filter((c) => Array.isArray(c) && c.length === 4 && Number.isFinite(c[0]) && Number.isFinite(c[1]) && c[1] > 0)
      .map((c) => ({ sum: c[0], n: c[1], maxRpm: c[2] || 0, shiftRpms: Array.isArray(c[3]) ? c[3].filter(Number.isFinite) : [], runMaxRpm: 0 }));
    l.accepted = o.accepted ?? 0;
    l.topRpm = o.topRpm ?? 0;
    l.msDriven = o.msDriven ?? 0;
    return l;
  }

  reset(): void {
    this.clusters = [];
    this.recent = [];
    this.engaged = -1;
    this.accepted = 0;
    this.topRpm = 0;
    this.msDriven = 0;
    this.lastT = 0;
    this.lastAcceptedT = 0;
  }
}
